import { Outlet } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import HelpWidget from '../components/HelpWidget';
import CommandPalette from '../components/CommandPalette';
import { useAdminData } from '../hooks/useAdminData';
import ErrorBoundary from '../components/ErrorBoundary';
import { PENDING_APP_STAGES } from '../constants/enums';
// Context + hook are in a separate module so this file can be a pure
// components-only module for Vite Fast Refresh. Keeping `useAdminDataContext`
// here broke HMR — every edit in the admin graph triggered a full invalidation
// instead of a hot update, stranding the page in a loading state.
import { AdminDataContext } from './adminDataContext';

export default function AdminLayout() {
  const adminData = useAdminData();

  // Force admin-dark theme for admin views — matches the landing hero +
  // login aesthetic. Tokens defined in src/App.css under [data-theme="admin-dark"].
  // No cleanup-restore: prev/cleanup pattern can leave theme stale during
  // sibling-route transitions (e.g., /admin → /client → /admin would
  // sometimes leak the inner route's theme). Each layout sets its own
  // theme on mount; the next layout's mount overrides — clean handoff.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'admin-dark');
  }, []);

  const pendingAppCount = (adminData.applications || []).filter(a => PENDING_APP_STAGES.includes(a.stage)).length;
  const alertCount = (adminData.visibleAlerts || []).length;

  return (
    <AdminDataContext.Provider value={adminData}>
      <div className="app">
        {/* A11y skip-link (B-13) — only visible when focused via keyboard Tab.
            Jumps past the sidebar chrome directly into the main content for
            screen-reader + keyboard-only users. */}
        <a
          href="#main-content"
          className="skip-link"
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            padding: '6px 12px',
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)',
            background: 'var(--accent-teal, #5DCAA5)',
            borderRadius: 6,
            textDecoration: 'none',
            zIndex: 10000,
            transform: 'translateY(-200%)',
            transition: 'transform 0.15s',
          }}
          onFocus={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          onBlur={(e) => { e.currentTarget.style.transform = 'translateY(-200%)'; }}
        >
          Skip to main content
        </a>
        <AdminSidebar pendingAppCount={pendingAppCount} alertCount={alertCount} />
        <main
          id="main-content"
          className="main-content"
          tabIndex={-1}
          style={{ padding: '24px 32px', minHeight: '100vh' }}
        >
          <ErrorBoundary label="Admin Page">
            <Suspense fallback={
              <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 11 }}>Loading...</div>
            }>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
        {/* Day 5: in-app help + command palette. Both self-gated on
            authenticated / app-route so they don't leak onto /login etc. */}
        <HelpWidget />
        <CommandPalette />
      </div>
    </AdminDataContext.Provider>
  );
}
